import {
  Badge,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import {
  INTL_DEVICE_LABELS,
  INTL_STAGE_LABELS,
  intlRequestNo,
  type IntlRequest,
  type IntlStage,
} from '@staff-app/shared-types';
import { CopyButton, trackingClipboard } from '@/components/CopyButton';
import { carrierTrackingUrl } from '../carrier-tracking';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER, DATA_TABLE_ROW } from '@/lib/data-table';
import { countryName } from '../hooks/useIntlRequests';
import { IntlInvoiceStatusBadge } from './IntlInvoiceStatusBadge';

/**
 * The staff filer, shortened to the part that identifies them.
 *
 * `@optisigns.com` on every row of a ten-column table costs ~70px of width to say the same nine
 * characters over and over — and this table did not fit a 14" laptop, so that width was being
 * paid for by the two columns scrolled off the right edge (requester, 2026-09-10).
 *
 * ONLY the house domain is stripped, and that is the whole rule: a filer from anywhere else is
 * shown in full, because for them the domain is the interesting half. The complete address stays
 * in the cell's `title` either way, so nothing is actually hidden — hovering, copying out of the
 * detail panel, and the CSV export all still carry it.
 */
export function staffShortEmail(email: string | null | undefined): string {
  const v = (email ?? '').trim();
  const m = /^([^@]+)@optisigns\.com$/i.exec(v);
  return m ? m[1] : v;
}

/**
 * Stage badge. Three states, three tones, and no invented fourth.
 *
 * The value is DERIVED server-side from the invoice stamps, so the UI renders what it is told
 * rather than re-deciding it here from the same fields and risking a different answer to the same
 * question. Same rule as DemoCouponTable's StatusBadge.
 */
/**
 * How long a FILED request may sit with nothing raised before the row says so, in days.
 *
 * ⚠️ THIS EXISTS BECAUSE FILING STOPPED BILLING (2026-09-20). Until then, filing drafted the
 * invoice automatically, and the argument for doing it inside `submit` was that two round trips
 * means the second can be lost — a rep who closes the tab leaves a request nobody knows is
 * unbilled. That objection was never wrong; it was answered by making the state VISIBLE rather
 * than impossible. This marker is that answer, and deleting it quietly re-opens the hole.
 *
 * THREE DAYS, not one: a request filed on Friday and quoted on Monday is ordinary work, and a
 * marker that fires on every fresh row is one people learn to ignore.
 */
const UNACTIONED_DAYS = 3;

function daysSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 86_400_000;
}

/**
 * "Filed N days ago and nothing raised."
 *
 * Only ever shown beside FILED, and only past `UNACTIONED_DAYS`. It is the one thing the queue
 * has to surface now that filing does not bill: a request at this stage has no quote and no
 * invoice, so no clock is running anywhere and nothing else in the system will mention it again
 * on its own.
 *
 * Muted rather than destructive — a nudge about work in progress, not a fault.
 */
function UnactionedMark({ request }: { request: IntlRequest }) {
  if (request.stage !== 'FILED') return null;
  const days = Math.floor(daysSince(request.submittedAt));
  if (days < UNACTIONED_DAYS) return null;
  return (
    <span
      className="ml-1.5 whitespace-nowrap text-xs text-muted-foreground"
      data-testid={`intl-unactioned-${request.id}`}
      title="Nothing has been quoted or invoiced for this request yet."
    >
      {`${days}d`}
    </span>
  );
}

function StageBadge({ stage }: { stage: IntlStage }) {
  /**
   * The tone follows how far along the row is — with one exception that is the whole point.
   *
   *   Filed / Quoted / Invoice draft   outline   still needs something from us
   *   Invoiced / Order created         secondary in flight
   *   Fulfilled                        default   done, and matches the `paid` invoice badge
   *   Fulfilled unpaid                 RED       hardware delivered and not paid for
   *
   * QUOTE EXPIRED is `outline` too, not `destructive`: a lapsed quote costs nothing and is fixed
   * by re-quoting. Styling it as an alarm would put it beside "hardware gone, money owed", which
   * is not the same kind of problem at all.
   *
   * `Fulfilled unpaid` is the only stage styled as a problem, and it is a real one: the goods
   * are in a customer's hands and the money is not. That is the set somebody chases and
   * eventually switches an account off over, so it should not look like the end of a happy path.
   */
  const variant =
    stage === 'FULFILLED_UNPAID'
      ? 'destructive'
      : stage === 'FULFILLED'
        ? 'default'
        // CANCELLED is `outline`, deliberately not `destructive`. Cancelling is a normal,
        // reversible act — it is how a wrong address gets fixed — so it must not wear the same
        // alarm as FULFILLED_UNPAID, which is money owed on goods already gone.
        : stage === 'INVOICED' || stage === 'ORDER_CREATED'
          ? 'secondary'
          : 'outline';
  return (
    <Badge variant={variant as never} data-testid={`intl-stage-${stage}`}>
      {INTL_STAGE_LABELS[stage] ?? stage}
    </Badge>
  );
}

/**
 * The international request ledger.
 *
 * Every column is somebody's question: which request (IR-n, the name it goes by in chat and
 * email), for whom, where to, what and how many, where it is in the lane, who filed it and when.
 *
 * THE INCOMPLETE-ADDRESS MARK IS STYLED AS A PROBLEM, on purpose and following the same rule the
 * RMA tab applies to an `UNKNOWN` device model. An address without street, city and phone cannot
 * be turned into a courier booking, so the row is not merely missing a nicety — it is blocked,
 * and the person who can most cheaply fix it is whoever is looking at this list.
 *
 * There is no money column. Nothing on this surface is priced yet, and a blank or zero currency
 * column would read as "free" rather than as "not quoted".
 *
 * IT FITS A 14" LAPTOP WITHOUT SCROLLING SIDEWAYS (requester, 2026-09-10), and that is a
 * constraint rather than an observation — ten columns is as many as this width holds. It was
 * 1216px wide in a 1135px container, so `Filed by` and `Filed` sat off the right edge until you
 * scrolled (`Filed` has since been removed altogether — see below). Three changes bought the 81px, each taking width from something that was not using
 * it: the filer lost a repeated `@optisigns.com`, the invoice cell stopped sizing itself for a
 * 27-character Stripe id it links to anyway, and the customer cell stopped reserving width for
 * an email too long to show regardless. Below 1400px `Filed by` also steps aside.
 *
 * SO A NEW COLUMN DOES NOT FIT AT EVERY WIDTH. Adding one means choosing what it replaces, or
 * making it responsive like `Filed by` — not appending it and letting the table scroll again.
 *
 * BOTH ROUTES HAVE NOW BEEN TAKEN, on the same day (2026-09-15), and the net is ten columns again:
 *
 *   + `Tracking`, the responsive route (requester: "Can I have the Tracking number in the table.
 *     Next to the invoice status"). It shares `Filed by`'s 1400px breakpoint, so below that both
 *     step aside and what remains is the set that was measured to fit.
 *   − `Filed`, the date, removed outright (requester: "I don't think I need the filed date in the
 *     table"). It is NOT lost — `Filed at` is on the detail panel and in the CSV, the same place
 *     `Filed by` went and for the same reason: a queue is scanned for what needs doing, and when
 *     a request was typed up is not that. Its width now pays for `Tracking`.
 *
 * So the budget is back where 2026-09-10 left it rather than spent: at ≥1400px this is ten
 * columns, and below it eight. Another column still has to choose a route.
 *
 * AND `Tracking` HAS TO STAY RESPONSIVE EVEN THOUGH `Filed` LEFT — measured, not assumed, in a
 * headed Chromium against the running app on 2026-09-15:
 *
 *   viewport   container   table    verdict
 *   1280px       990px      990px   fits (8 columns)
 *   1280px       990px     1033px   OVERFLOWS BY 43px — with `Tracking` always visible
 *   1400px      1110px     1110px   fits (10 columns)
 *   1512px      1222px     1222px   fits (10 columns)
 *
 * It is tempting to read "`Filed` is gone, so there is room now" — there is not. The remaining
 * cells are `whitespace-nowrap`, so they stop compressing at their content and the table pushes
 * past the container instead. `Filed`'s width paid for `Tracking` ABOVE the breakpoint, which is
 * what keeps the ten-column case honest; below it, eight is still the number that fits.
 *
 * AND NO ACTION COLUMN, as of 2026-09-10 (requester: "I don't think I need to have the refresh
 * icon and Invoice button on the right of filed column"). It held two buttons and both have been
 * overtaken: the ten-minute poll re-reads every live invoice, so nobody needs to ask Stripe per
 * row, and the detail panel carries Raise and Refresh next to the invoice they act on with the
 * context to judge them. A queue whose every row offers a button invites pressing one; this one
 * is now for reading, and clicking a row is how you get to the doing.
 */
export function IntlRequestTable({
  requests,
  onSelect,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: {
  requests: IntlRequest[];
  onSelect?: (r: IntlRequest) => void;
  /**
   * ── PICKING ROWS TO EXPORT (requester, 2026-09-17) ───────────────────────────────────────
   *
   * Optional, all three together: the column only appears where a parent is holding the
   * selection. The table stays a pure display otherwise, which is what lets it be rendered in a
   * spec with two props and no state.
   */
  selectedIds?: ReadonlySet<number>;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: (ids: number[], select: boolean) => void;
}) {
  const selectable = Boolean(selectedIds && onToggleSelect);
  const pageIds = requests.map((r) => r.id);
  // "All" means all the rows ON SCREEN, not every request that matches the filter — the header
  // box can only speak for what it can see, and claiming otherwise would export rows the
  // operator never looked at.
  const allOnPage = selectable && pageIds.length > 0 && pageIds.every((id) => selectedIds!.has(id));
  if (!requests.length) {
    return (
      <p className="p-6 text-sm text-muted-foreground" data-testid="intl-requests-empty">
        {'No international requests match.'}
      </p>
    );
  }

  return (
    <Table className={DATA_TABLE_CELLS} data-testid="intl-requests-table">
      <TableHeader className={DATA_TABLE_HEADER}>
        <TableRow>
          {selectable ? (
            <TableHead className="w-8">
              <Checkbox
                checked={allOnPage}
                onCheckedChange={(v) => onToggleSelectAll?.(pageIds, v === true)}
                aria-label="Select every request on this page"
                data-testid="intl-select-all"
              />
            </TableHead>
          ) : null}
          <TableHead className="whitespace-nowrap">{'Request'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Customer'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Destination'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Item'}</TableHead>
          <TableHead className="whitespace-nowrap text-right">{'Qty'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Stage'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Invoice'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Invoice status'}</TableHead>
          {/* Amazon's tracking, on the SAME 1400px breakpoint as `Filed by` — see the header
              comment. Placed here because it was asked for next to the invoice status, and that
              is also where it reads best: "they paid" and "it shipped" are the two halves of the
              same question, and the stage column already carries our half of it. */}
          <TableHead className="hidden whitespace-nowrap min-[1400px]:table-cell">
            {'Tracking'}
          </TableHead>
          {/* HIDDEN BELOW 1400px — the other column that steps aside, and the reason it is this
              one: the toolbar's `Mine` toggle already answers "which of these are mine", and the
              detail panel and the CSV both carry the filer. Losing it costs a hover; the
              alternative was the whole table scrolling sideways on a 14" laptop at 150% scaling
              (1280×800 → 975px of container against 1066px of table). `Tracking` now shares this
              breakpoint, and is hidden at the same widths for the same reason.

              An arbitrary breakpoint rather than `xl:`, which is 1280 and therefore ACTIVE at
              exactly the width that needs the column gone. 1400 is between the two sizes that
              matter: hidden at 1280, shown at 1440 and on the 14" MacBook's 1512. */}
          <TableHead className="hidden whitespace-nowrap min-[1400px]:table-cell">
            {'Filed by'}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((r) => (
          <TableRow
            key={r.id}
            className={`${DATA_TABLE_ROW} ${onSelect ? 'cursor-pointer' : ''}`}
            onClick={onSelect ? () => onSelect(r) : undefined}
            data-testid={`intl-row-${r.id}`}
          >
            {selectable ? (
              // `stopPropagation` because the row opens the drawer on click: ticking a box to
              // export it must not also open the panel for it.
              <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds!.has(r.id)}
                  onCheckedChange={() => onToggleSelect?.(r.id)}
                  aria-label={`Select ${intlRequestNo(r.id)}`}
                  data-testid={`intl-select-${r.id}`}
                />
              </TableCell>
            ) : null}
            <TableCell className="whitespace-nowrap font-mono text-xs">
              {intlRequestNo(r.id)}
            </TableCell>
            {/* 170, down from 220. The email under the company is longer than any cap would
                show — `paul.sanbox08232026+billing3@optisigns.com` needs 268px — so the choice
                was never "whole or truncated", only how much width to spend on a value that
                truncates either way. The company name, which is what the eye scans for, still
                fits; the full email is in the detail panel and the CSV. */}
            <TableCell className="max-w-[170px]">
              <div className="truncate text-foreground">{r.company || r.contactName || '—'}</div>
              {/* The contact, not the company, is who somebody emails — so it is shown rather
                  than hidden behind a click. */}
              <div className="truncate text-xs text-muted-foreground">{r.contactEmail}</div>
            </TableCell>
            <TableCell className="max-w-[200px]">
              <div className="truncate">{countryName(r.shipToCountry)}</div>
              {!r.addressComplete ? (
                /* NAMES THE MISSING FIELDS. "Address incomplete" on its own sent somebody to ask
                   which field it meant — for IR-12 it was the phone and everything else was
                   filled. A badge that states a problem without naming it costs a round trip
                   every time, and the fix is one word from data the server already derives. */
                <div
                  className="mt-0.5 flex items-start gap-1 text-xs text-destructive"
                  data-testid={`intl-address-incomplete-${r.id}`}
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    {r.addressMissing?.length
                      ? `Needs ${r.addressMissing.join(' + ')}`
                      : 'Address incomplete'}
                  </span>
                </div>
              ) : (
                <div className="truncate text-xs text-muted-foreground">
                  {[r.shipToCity, r.shipToPostalCode].filter(Boolean).join(' ') || '—'}
                </div>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {INTL_DEVICE_LABELS[r.deviceModel] ?? r.deviceModel}
            </TableCell>
            <TableCell className="whitespace-nowrap text-right font-mono">{r.quantity}</TableCell>
            <TableCell className="whitespace-nowrap">
              <StageBadge stage={r.stage} />
              <UnactionedMark request={r} />
            </TableCell>
            {/*
                CAPPED AT 132px, down from 200 (requester, 2026-09-10 — the table had to fit a
                14" laptop and was 81px too wide).

                The 200 was sized for the WIDEST thing this cell can hold: the raw
                `in_1UDn5gC6U6a5yHiPlM8nwIcU` id, 27 mono characters, shown while Stripe has not
                yet assigned a customer-facing number. That case is what pushed two columns off
                the right edge of the screen, and it is the case that needs the width least — the
                cell is already a LINK to the invoice, so nobody has to read the id to reach it.
                A real invoice number (`VAAEDZ3P-0012`) still shows whole.

                A FIXED cap rather than one that yields to content, deliberately: with `table-auto`
                the column is as wide as its widest row, so a single failed invoice or one
                un-numbered draft would silently widen the table again and un-fit it. Every branch
                below therefore truncates at the same width and puts the full text in `title`.
            */}
            <TableCell className="max-w-[132px] font-mono text-xs">
              {/* The customer-facing NUMBER when Stripe has assigned one (what they quote back),
                  falling back to the id (what staff paste into Stripe). Null until finalized. */}
              {r.invoiceNumber || r.stripeInvoiceId ? (
                // LINKED, because reviewing the draft before it sends is the whole point of the
                // window — and the queue is where a rep comes back to it. `stopPropagation` so
                // opening Stripe does not also trigger the row.
                r.invoiceUrl ? (
                  <a
                    href={r.invoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title={r.invoiceNumber || r.stripeInvoiceId || undefined}
                    className="inline-flex max-w-full items-center gap-1 text-primary underline"
                    data-testid={`intl-invoice-link-${r.id}`}
                  >
                    {/* `min-w-0` is what lets a flex child truncate at all — without it the
                        anchor grows to its content and the cap does nothing. */}
                    <span className="min-w-0 truncate">
                      {r.invoiceNumber || r.stripeInvoiceId}
                    </span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  <span
                    className="block truncate"
                    title={r.invoiceNumber || r.stripeInvoiceId || undefined}
                  >
                    {r.invoiceNumber || r.stripeInvoiceId}
                  </span>
                )
              ) : r.invoiceError ? (
                /* Filing drafts the invoice, so a row with NO invoice and an error is a row where
                   that failed — and it is the one state on this table that needs a person. Styled
                   as a problem for the same reason the incomplete address is. */
                <span
                  className="flex items-start gap-1 whitespace-normal text-destructive"
                  data-testid={`intl-invoice-failed-${r.id}`}
                  title={r.invoiceError}
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span className="line-clamp-2 font-sans">{r.invoiceError}</span>
                </span>
              ) : (
                '—'
              )}
            </TableCell>
            {/* Stripe's word, on its own axis — see InvoiceStatusBadge. */}
            <TableCell className="whitespace-nowrap">
              <IntlInvoiceStatusBadge status={r.invoiceStatus} />
            </TableCell>
            {/*
                THE FIRST tracking number, not all of them. `mcfPackages` is an array because
                Amazon splits an order across shipments and packages — 600 units is many boxes —
                and a cell that grew a line per box would make one row as tall as five. `+N` says
                there are more and the drawer lists them with their carrier, Amazon shipment id,
                fulfilment centre and ETA.

                FIXED cap, same rule as the invoice cell: with `table-auto` a column is as wide as
                its widest row, so one long number would silently widen the table and un-fit it.

                A package can exist with NO tracking number — Amazon creates the shipment before
                the carrier assigns one — so the first package is not necessarily the first
                TRACKABLE one. Picking by `trackingNumber` rather than by index is what stops a
                row showing "—  +2" while two numbers sit behind it.

                BOTH ROUTES (requester, 2026-09-17). A request fulfilled from China has no
                `mcfPackages` at all — recording the number IS the act, there is no Amazon
                shipment behind it — so a cell that read only the Amazon array showed "—" on a
                row whose stage says FULFILLED.

                AMAZON IS READ FIRST and China only falls through behind it, which is the
                requester's own ordering. The two almost never coexist: a China shipment normally
                means no Amazon order was ever placed. When they do, Amazon leads and China sits
                inside the `+N`.

                WHICH ROUTE is in the `title` rather than on screen. The column is capped at
                150px against a measured layout (see the header docblock — it overflows by 43px
                at 1280 if anything grows), so a badge or a second line here is width this table
                does not have. The drawer names the route in full.

                IT IS A LINK NOW (requester, 2026-09-17). It used to be plain text because
                Amazon gives a `carrierCode` and never a URL; `carrier-tracking.ts` turns that
                code into one, so an anchor is honest here.

                NO EXTERNAL-LINK ICON, unlike the drawer — that icon was the part this column
                cannot afford. The anchor is the same glyphs as the span it replaces, so it costs
                ZERO width against the measured layout in the header docblock; an icon plus its
                gap would not be. Colour and hover-underline carry it instead.

                An unrecognised carrier still renders plain text — no link is better than a wrong
                one, and the number stays copyable either way. `stopPropagation` on BOTH the
                anchor and the copy button, because either one inside a clickable row must not
                also open the drawer.
            */}
            <TableCell className="hidden max-w-[150px] font-mono text-xs min-[1400px]:table-cell">
              {(() => {
                // AMAZON FIRST, CHINA BEHIND IT (requester, 2026-09-17: "always reach the
                // Amazon package first. If user enter the china fulfillment, then take it to
                // show up"). So the cell reads the Amazon array and falls through to the China
                // number only when that array has nothing trackable in it — which is the normal
                // case for a China shipment, since recording the number IS the act and no
                // Amazon shipment exists behind it. When both routes exist, Amazon leads and
                // China is inside the `+N`.
                // `marketplace` rides along because the swiship host is REGIONAL — a DE parcel
                // tracks on swiship.de, and the wrong host reports "not found" for a parcel that
                // is moving normally. The China entry carries none on purpose: no Amazon
                // marketplace shipped it.
                const tracked: {
                  number: string;
                  via: string;
                  carrier?: string | null;
                  marketplace?: string | null;
                }[] = [
                  ...(r.mcfPackages ?? [])
                    .filter((p) => p.trackingNumber)
                    .map((p) => ({
                      number: p.trackingNumber!,
                      via: 'Amazon',
                      carrier: p.carrierCode,
                      marketplace: r.mcfMarketplace,
                    })),
                  ...(r.chinaTrackingNumber
                    ? [{ number: r.chinaTrackingNumber, via: 'China', carrier: r.chinaCarrier }]
                    : []),
                ];
                if (!tracked.length) return '—';
                const [first, ...rest] = tracked;
                const url = carrierTrackingUrl(first.carrier, first.number, first.marketplace);
                const title = `${first.number} — shipped via ${first.via}`;
                return (
                  <div className="flex items-center gap-1">
                    {/* `min-w-0` is what lets a flex child truncate at all — on the anchor too,
                        or a long number stops truncating and widens the column. */}
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(e) => e.stopPropagation()}
                        className="min-w-0 truncate text-primary hover:underline"
                        title={title}
                        data-testid={`intl-tracking-${r.id}`}
                      >
                        {first.number}
                      </a>
                    ) : (
                      <span
                        className="min-w-0 truncate"
                        title={title}
                        data-testid={`intl-tracking-${r.id}`}
                      >
                        {first.number}
                      </span>
                    )}
                    <CopyButton
                      {...trackingClipboard(first.number, url)}
                      label="Tracking number"
                      stopPropagation
                      testId={`intl-copy-tracking-${r.id}`}
                    />
                    {rest.length ? (
                      <span
                        className="shrink-0 font-sans text-muted-foreground"
                        title={`${tracked.length} tracking numbers — open the row for all of them`}
                        data-testid={`intl-tracking-more-${r.id}`}
                      >
                        {`+${rest.length}`}
                      </span>
                    ) : null}
                  </div>
                );
              })()}
            </TableCell>
            {/* `title` carries the full address — see staffShortEmail. Same breakpoint as its
                header, or the body and the header would disagree about how many columns exist. */}
            <TableCell
              className="hidden max-w-[140px] truncate text-xs min-[1400px]:table-cell"
              title={r.submittedBy}
            >
              {staffShortEmail(r.submittedBy)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
